import { SfLog } from '@app/adapters/logger'

// https://docs.appcues.com/flow-events-reference-developer
// Base type for all flow-level events (flow_started, flow_completed, flow_skipped, flow_aborted)
export interface FlowEventPayload {
  id: string
  name: string
  flowId: string
  flowName: string
  flowVersion: number
  sessionId: number
  timestamp: number
}

// Flow error events (flow_error, flow_attempted with errors)
export interface FlowErrorPayload extends FlowEventPayload {
  error: string
  details: string
}

// Base type for all step-level events (step_started, step_completed, step_skipped, step_aborted)
export interface StepEventPayload extends FlowEventPayload {
  stepId: string
  stepType: string
}

// Step error events (step_error, step_attempted with errors)
export interface StepErrorEventPayload extends StepEventPayload {
  error: string
  details: string
}

// Union type covering all Appcues event payloads
export type AppcuesEventPayload =
  | FlowEventPayload
  | FlowErrorPayload
  | StepEventPayload
  | StepErrorEventPayload

interface AppcuesSettings {
  zapierWebhookUrl?: string
  userId?: string
}

interface ErrorData {
  name: string
  flowId: string
  flowName: string
  flowVersion: number
  timestamp: number
  sessionId: number
  errorMessage: string
  details: string
  stepId?: string
  stepType?: string
  userId: string
  eventType: string
}

export function setupAppcuesErrorMonitoring(): void {
  if (!window.Appcues) {
    return
  }

  const settings = (window as { AppcuesSettings?: AppcuesSettings })
    .AppcuesSettings

  if (!settings?.zapierWebhookUrl || !settings?.userId) {
    return
  }

  const { zapierWebhookUrl: webhookUrl, userId } = settings

  const sendErrorToZapier = (
    eventType: string,
    payload: FlowErrorPayload | StepErrorEventPayload
  ): void => {
    try {
      const errorPayload = payload
      const errorData: ErrorData = {
        name: errorPayload?.name,
        flowId: errorPayload?.flowId,
        flowName: errorPayload?.flowName,
        flowVersion: errorPayload?.flowVersion,
        sessionId: errorPayload?.sessionId,
        timestamp: errorPayload?.timestamp,
        errorMessage: errorPayload?.error,
        details: errorPayload?.details,
        stepId: 'stepId' in errorPayload ? errorPayload.stepId : undefined,
        stepType:
          'stepType' in errorPayload ? errorPayload.stepType : undefined,
        userId,
        eventType,
      }

      fetch(webhookUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorData),
      }).catch((err) => {
        SfLog.error('Failed to send Appcues error to Zapier', { error: err })
      })
    } catch (err) {
      SfLog.error('Error in Appcues error handler', { error: err })
    }
  }

  window.Appcues.on('flow_error', (payload) =>
    sendErrorToZapier('flow_error', payload as FlowErrorPayload)
  )
  window.Appcues.on('step_error', (payload) =>
    sendErrorToZapier('step_error', payload as StepErrorEventPayload)
  )
}
